/** @jsx isolateComponent */

import { ReactNode } from 'react';
import {
  Colors,
  ColorValue,
  Typography,
} from '@zapier/design-system/src/theme';
import Link from '@zapier/design-system/src/components/navigation/Link';
import { kebabCaseAriaAttrs } from '@zapier/design-system/src/utils/formatAriaAttrKeys';
import {
  hexToRrggbbaa,
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

type BadgeColorVariant = {
  base: {
    backgroundColor: string;
    color: ColorValue;
  };
  hover: {
    backgroundColor: string;
    color: ColorValue;
  };
};
const BadgeVariants = {
  light: {
    base: {
      backgroundColor: hexToRrggbbaa(Colors.black, 0.05),
      color: Colors.black,
    },
    hover: {
      backgroundColor: hexToRrggbbaa(Colors.blue100, 1),
      color: Colors.blue500,
    },
  },
  white: {
    base: {
      backgroundColor: hexToRrggbbaa(Colors.white, 1),
      color: Colors.black,
    },
    hover: {
      backgroundColor: hexToRrggbbaa(Colors.black, 0.05),
      color: Colors.blue500,
    },
  },
  grey: {
    base: {
      backgroundColor: hexToRrggbbaa(Colors.black, 0.1),
      color: Colors.grey700,
    },
    hover: {
      backgroundColor: hexToRrggbbaa(Colors.black, 0.1),
      color: Colors.grey700,
    },
  },
  dark: {
    base: {
      backgroundColor: hexToRrggbbaa(Colors.white, 0.3),
      color: Colors.white,
    },
    hover: {
      backgroundColor: hexToRrggbbaa(Colors.white, 0.2),
      color: Colors.white,
    },
  },
};

type BadgeProps = {
  /** The color variant of the badge. */
  color?: keyof typeof BadgeVariants;
  /** Make badge clickable with link. */
  href?: string;
  /** Anchor link target. */
  hrefTarget?: '_self' | '_blank' | '_parent' | '_top';
  /** Text to render inside the badge. */
  children: ReactNode;
  /** Indicates the element represents the current item within a list. */
  ariaCurrent?: 'page' | false;
};

const defaultProps = {
  color: 'light' as keyof typeof BadgeVariants,
};

type BadgePropsWithDefaults = BadgeProps & typeof defaultProps;

const Styles = labelStyles('Badge', {
  root: (
    props: BadgePropsWithDefaults,
    badgeColorVariant: BadgeColorVariant
  ) => [
    {
      ...Typography.altText900,
      fontWeight: 600,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 30,
      padding: '0 15px',
      borderRadius: 20,
    },
    withModifier(props.color, {
      backgroundColor: badgeColorVariant.base.backgroundColor,
      color: badgeColorVariant.base.color,
    }),
    props.href &&
      withModifier('as-link', {
        '&:hover, &:focus': {
          backgroundColor: badgeColorVariant.hover.backgroundColor,
          color: badgeColorVariant.hover.color,
        },
      }),
  ],
});

/**
 * Renders a badge component. Used for breadcrumbs, etc.
 *
 * Tags: core-ui
 */
const Badge: React.FC<BadgeProps> = _props => {
  const props: BadgePropsWithDefaults = {
    ...defaultProps,
    ..._props,
  };
  const badgeVariant = BadgeVariants[props.color];
  const css = Styles.root(props, badgeVariant);
  const spanProps = {
    ...kebabCaseAriaAttrs(props),
    css,
  };
  const linkProps = {
    ...spanProps,
    target: props.hrefTarget,
    href: props.href as string,
  };
  return props.href !== undefined ? (
    <Link {...linkProps}>{props.children}</Link>
  ) : (
    <span {...spanProps}>{props.children}</span>
  );
};

Badge.defaultProps = {
  color: 'light' as 'light',
};

export default Badge;
